import { Conversation } from "./conversation";
import { ChatPane, buildChatPane, uiStrings } from "./pane";
import { WidgetConfig } from "./remote_config";
import { injectStyles } from "./styles";

/**
 * A chat window placed in the page: `<div data-amai-chat></div>`, or
 * `AmaiChat.init({ container })`. The same chat as the floating bubble
 * (which stays): both are windows on the page's one Conversation.
 *
 *   - Before the visitor writes it shows their open chat, or the greeting
 *     with the quick replies as suggested questions. Nothing is created
 *     on the server until they send something (Conversation.showHistory).
 *   - Compact until the visitor's chat begins: as tall as the greeting,
 *     the suggestions and the input (or --amai-chat-idle-height). With
 *     the first message, typed or a tapped suggestion, it grows to its
 *     height (below). A returning visitor's chat opens at that height.
 *     The container says which in data-amai-chat-state ("idle" or
 *     "active", once known: after the history lookup) and with an
 *     `amai-chat:state` event, for the page to style or animate around
 *     it, or to keep room for the window until then.
 *   - A fixed height, set by the page (--amai-chat-height on the
 *     container, 600px by default, 70vh on phones); the messages scroll
 *     inside it and the page never jumps. Nothing takes the focus on load.
 *   - As wide as the container, or --amai-chat-width (and while compact
 *     --amai-chat-idle-width), never wider; the first message grows the
 *     width along with the height. A narrower window sits at the start,
 *     or where --amai-chat-align (start, center or end) says.
 *   - On a phone, focusing a field opens the chat full-screen, as the
 *     bubble's popover is there. That full-screen view is a window of its
 *     own on <body>: a box inside a transformed or filtered section can't
 *     cover the screen itself. The rest of the page is inert meanwhile, so
 *     neither the focus nor a screen reader ends up behind it. Its × or
 *     Escape brings the visitor back.
 *   - A window hidden on load (a closed tab or dialog) scrolls to its
 *     newest message once it shows.
 */
export interface EmbedMount {
    destroy: () => void;
}

/** Where the bubble's popover goes full-screen as well. */
const PHONE_QUERY = "(max-width: 480px)";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Before the visitor's chat begins: compact. */
type EmbedState = "idle" | "active";

/** On the page's container: the window's state, for the page's CSS. */
const STATE_ATTRIBUTE = "data-amai-chat-state";

/** Dispatched on the page's container (it bubbles) when the state is
 *  known and when it changes, with `detail: { state }`. */
const STATE_EVENT = "amai-chat:state";

/** The box while it is compact (styles.ts). */
const IDLE_CLASS = "amai-chat-embed-idle";

/** Where the page puts a box narrower than its container: start (the
 *  default, no class), center or end. */
const ALIGN_PROPERTY = "--amai-chat-align";
const ALIGN_CLASSES: Record<string, string> = {
    center: "amai-chat-embed-center",
    end: "amai-chat-embed-end",
};

/** How long the box takes to grow to its full size. */
const GROW_MS = 260;

/** Numbers the windows on a page, for their element ids. */
let embedCount = 0;

export function mountEmbed(params: {
    /** The page's element the window goes in. */
    host: HTMLElement;
    config: WidgetConfig;
    conversation: Conversation;
    baseUrl: string;
    widgetToken: string;
    locale: string;
}): EmbedMount {
    const { host, config, conversation, baseUrl, widgetToken, locale } = params;
    const ui = uiStrings(locale);
    const embedId = ++embedCount;

    injectStyles(config.primary_color);

    const box = document.createElement("div");
    // Compact until it is known that the visitor's chat has begun.
    box.className = `amai-chat-embed ${IDLE_CLASS}`;
    box.setAttribute("role", "region");
    // Focusable from code only: where the visitor lands back after the
    // full-screen view.
    box.tabIndex = -1;
    const titleId = `amai-chat-embed-title-${embedId}`;
    box.setAttribute("aria-labelledby", titleId);
    host.appendChild(box);
    align();
    // The page's alignment may change with the screen's width.
    window.addEventListener("resize", align);

    const pane: ChatPane = buildChatPane({
        frame: box,
        config,
        ui,
        locale,
        baseUrl,
        widgetToken,
        titleId,
        // Not shown in the page (styles.ts): there is nothing to close.
        closeLabel: ui.closeChat,
        onClose: () => undefined,
        onSend: (text, answering) => {
            void conversation.send(text, pane, answering);
        },
        // A field a reply brings takes the focus only while the window is
        // on screen, and never moves the page. Not on a phone: there the
        // focus opens the full-screen view, and that's the visitor's call.
        mayFocus: () => !isPhone() && onScreen(box),
        focusOptions: { preventScroll: true },
    });
    conversation.attach(pane);
    void conversation.showHistory();

    // A list can't scroll while it isn't drawn: a window in a closed tab,
    // accordion or dialog scrolls to the end when it gets its size. A
    // width the page sets after the window went in (a stylesheet loaded
    // late, a class set by a script) often brings an alignment: the box
    // moves there with it, not only when it grows.
    const resizes =
        typeof ResizeObserver === "function"
            ? new ResizeObserver(() => {
                  pane.revealed();
                  align();
              })
            : undefined;
    resizes?.observe(box);

    let fullScreen:
        | { frame: HTMLDivElement; pane: ChatPane; inerted: Element[] }
        | undefined;

    /** Undefined until it is known: the page's container gets no
     *  state while the history lookup is out. */
    let state: EmbedState | undefined;
    /** The growth's next frame. */
    let growing: number | undefined;
    // In the same task as the box went in: never drawn otherwise.
    if (conversation.begun !== undefined) {
        setState(conversation.begun ? "active" : "idle");
    }
    const stopWatching = conversation.onBegunChange((begun, loaded) => {
        if (!begun) setState("idle");
        else if (loaded) setState("active");
        else grow();
    });

    function setState(next: EmbedState): void {
        if (state === next) return;
        state = next;
        stopGrowing();
        box.classList.toggle(IDLE_CLASS, next === "idle");
        host.setAttribute(STATE_ATTRIBUTE, next);
        // A page may align it by the state too.
        align();
        host.dispatchEvent(
            new CustomEvent(STATE_EVENT, {
                bubbles: true,
                detail: { state: next },
            }),
        );
    }

    /** --amai-chat-align, as the box inherits it from the page. */
    function align(): void {
        const value = getComputedStyle(box)
            .getPropertyValue(ALIGN_PROPERTY)
            .trim();
        for (const [name, className] of Object.entries(ALIGN_CLASSES)) {
            box.classList.toggle(className, value === name);
        }
    }

    /**
     * The visitor's chat began just now: the box grows from its compact
     * size to its full one, downward from its top, at once with
     * prefers-reduced-motion; a compact width of its own changes in the
     * same steps as the height. In the window the visitor sent from, the
     * page follows as little as it can. With the box's top on screen the
     * page scrolls only as far as the input needs to be in view, never
     * so far that the top leaves the screen; an input that was partly
     * below the screen comes up with the growth. With the top above the
     * screen, the input stays where it is.
     */
    function grow(): void {
        if (state === "active") return;
        const from = box.getBoundingClientRect();
        const visible =
            from.height > 0 && from.bottom > 0 && from.top < window.innerHeight;
        const follow = visible && !fullScreen && conversation.isActive(pane);
        setState("active");
        const to = box.getBoundingClientRect();
        // Whether it widens, measured at its compact height: at its full
        // height the page may get a scrollbar for this measure only (on
        // screen it comes as the box grows), and a box as wide as a
        // container the scrollbar narrows would grow sideways although
        // the page set no width.
        box.style.height = `${from.height}px`;
        const widen = box.getBoundingClientRect().width !== from.width;
        box.style.height = "";
        if (
            !visible ||
            fullScreen ||
            (to.height <= from.height && !widen) ||
            typeof requestAnimationFrame !== "function" ||
            matches(REDUCED_MOTION_QUERY)
        ) {
            if (follow) keepInView(from, 1);
            return;
        }
        let start: number | undefined;
        const step = (now: number): void => {
            start ??= now;
            const t = Math.min(1, (now - start) / GROW_MS);
            const grown = easeOut(t);
            // Done: the stylesheet's size again, which follows the page
            // (70vh on a phone that turns, a container that resizes).
            box.style.height =
                t < 1
                    ? `${from.height + (to.height - from.height) * grown}px`
                    : "";
            if (widen) {
                box.style.width =
                    t < 1
                        ? `${from.width + (to.width - from.width) * grown}px`
                        : "";
            }
            if (follow) keepInView(from, grown);
            growing = t < 1 ? requestAnimationFrame(step) : undefined;
        };
        box.style.height = `${from.height}px`;
        if (widen) box.style.width = `${from.width}px`;
        growing = requestAnimationFrame(step);
    }

    /** `from`: where the box was before it grew; `grown`: how far it has
     *  grown, from 0 to 1. */
    function keepInView(from: DOMRect, grown: number): void {
        const now = box.getBoundingClientRect();
        let by: number;
        if (from.top < 0) {
            by = now.bottom - from.bottom;
        } else {
            // The input sits at the box's bottom. What of it was below
            // the screen (often only the box's padding, on a phone the
            // input row under the suggestion the visitor tapped) comes up
            // as the box grows, so it never drops further out of view.
            const below =
                Math.max(0, from.bottom - window.innerHeight) * (1 - grown);
            by = Math.min(now.bottom - window.innerHeight - below, now.top);
        }
        if (by > 0) scrollPageBy(by);
    }

    function stopGrowing(): void {
        if (growing === undefined) return;
        cancelAnimationFrame(growing);
        growing = undefined;
        box.style.height = "";
        box.style.width = "";
    }

    box.addEventListener("focusin", (e) => {
        conversation.activate(pane);
        const target = e.target;
        if (target instanceof HTMLInputElement && isPhone()) {
            openFullScreen(target);
        }
    });

    function openFullScreen(from: HTMLInputElement): void {
        if (fullScreen) return;
        const frame = document.createElement("div");
        frame.className = "amai-chat-embed amai-chat-embed-fullscreen";
        frame.setAttribute("role", "dialog");
        frame.setAttribute("aria-modal", "true");
        const fullTitleId = `${titleId}-full`;
        frame.setAttribute("aria-labelledby", fullTitleId);
        document.body.appendChild(frame);

        const fullPane: ChatPane = buildChatPane({
            frame,
            config,
            ui,
            locale,
            baseUrl,
            widgetToken,
            titleId: fullTitleId,
            closeLabel: ui.exitFullScreen,
            onClose: closeFullScreen,
            onSend: (text, answering) => {
                void conversation.send(text, fullPane, answering);
            },
        });
        // Everything else on the page, the bubble and this window
        // included, is behind it: out of the focus order and hidden from
        // screen readers, as aria-modal promises. What the page made
        // inert itself stays so.
        const inerted = Array.from(document.body.children).filter(
            (el) => el !== frame && !el.hasAttribute("inert"),
        );
        for (const el of inerted) el.setAttribute("inert", "");
        fullScreen = { frame, pane: fullPane, inerted };
        conversation.attach(fullPane);
        conversation.activate(fullPane);
        frame.addEventListener("focusin", () => {
            conversation.activate(fullPane);
        });
        document.addEventListener("keydown", onKeydown);

        // Both windows show the same messages in the same order, so the
        // field the visitor tapped has its twin at the same position.
        const fields = Array.from(box.querySelectorAll("input"));
        const twin = frame.querySelectorAll("input")[fields.indexOf(from)];
        (twin ?? fullPane.inputField).focus();
    }

    // On the document, wherever the focus is: the view is the whole
    // screen.
    function onKeydown(e: KeyboardEvent): void {
        // The address dropdown and the photo gallery close first.
        if (e.key !== "Escape" || e.defaultPrevented) return;
        if (fullScreen?.pane.galleryOpen()) return;
        closeFullScreen();
    }

    function closeFullScreen(): void {
        if (!removeFullScreen()) return;
        conversation.activate(pane);
        box.focus();
    }

    /** False when there was none. */
    function removeFullScreen(): boolean {
        if (!fullScreen) return false;
        const { frame, pane: fullPane, inerted } = fullScreen;
        fullScreen = undefined;
        document.removeEventListener("keydown", onKeydown);
        for (const el of inerted) el.removeAttribute("inert");
        fullPane.closeGallery();
        conversation.detach(fullPane);
        frame.remove();
        return true;
    }

    function destroy(): void {
        removeFullScreen();
        stopWatching();
        stopGrowing();
        window.removeEventListener("resize", align);
        host.removeAttribute(STATE_ATTRIBUTE);
        resizes?.disconnect();
        pane.closeGallery();
        conversation.detach(pane);
        box.remove();
    }

    return { destroy };
}

function isPhone(): boolean {
    return matches(PHONE_QUERY);
}

function matches(query: string): boolean {
    return (
        typeof window.matchMedia === "function" &&
        window.matchMedia(query).matches
    );
}

function easeOut(t: number): number {
    return 1 - Math.pow(1 - t, 3);
}

/** Down by `by` pixels, at once also where the page scrolls smoothly:
 *  the growth moves in steps of a frame. */
function scrollPageBy(by: number): void {
    try {
        window.scrollBy({ top: by, behavior: "instant" });
    } catch {
        // A browser that doesn't know "instant" yet.
        window.scrollBy(0, by);
    }
}

/** Some of `el` is in the viewport. */
function onScreen(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
}
